import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  class: "w-full flex flex-col gap-8 text-left",
  "data-testid": "approvals-hub"
}
const _hoisted_2 = ["data-testid"]
const _hoisted_3 = { class: "text-base leading-6 font-bold text-textPrimary" }
const _hoisted_4 = { class: "text-sm leading-[18px] text-neutral50" }
const _hoisted_5 = { class: "grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-3" }
const _hoisted_6 = ["data-testid"]
const _hoisted_7 = { class: "text-base leading-6 font-bold text-textPrimary" }
const _hoisted_8 = { class: "text-sm leading-[18px] text-neutral50" }

import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useApprovalsHubStore } from '@/pinia-store/useApprovalsHubStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import type { Authorizations } from '@/types/authorization'

import ApprovalPanelCard from './components/ApprovalPanelCard.vue'
import ApprovalQueueCard from './components/ApprovalQueueCard.vue'
import {
  APPROVALS_QUEUE_GROUPS,
  approvalsQueueCard,
  queuesInGroup,
  visibleApprovalsPanels,
  visibleApprovalsQueues
} from './queues'
import type { ApprovalsQueue, ApprovalsQueueKey } from './queues'


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const router = useRouter()
const headerStore = useHeaderStore()
const hubStore = useApprovalsHubStore()
const { flags } = useFeatureFlags()

// Q27 — a fila "Avaliação de Evidências" some do hub quando o backend informa
// que a inserção manual está bloqueada. A ROTA continua viva: quem tem a URL
// salva cai na tela com a faixa de aposentada, nunca num 404.
const dsAttendanceStore = useDsAttendanceStore()
const permissionsStore = usePermissionsStore()

// Mesma regra da guarda de rota (OR entre as autorizações da fila): card só da
// fila que o usuário consegue abrir — e contador só dela.
const canAccess = (authorizations: Authorizations) =>
  authorizations.some((authorization) => permissionsStore.checkAnyRessources(authorization))

const queues = computed(() => {
  const isRetired = dsAttendanceStore.isManualCheckBlocked
  return visibleApprovalsQueues(flags, { isCheckValidationRetired: isRetired, canAccess }).map(
    (queue) => approvalsQueueCard(queue, isRetired)
  )
})
// Painéis (Pós-plantão) vindos de main (#2729): card único, fora dos grupos de fila.
const panels = computed(() => visibleApprovalsPanels(flags))

// Grupo sem fila ligada não aparece (evita título órfão quando as flags do grupo
// inteiro estão desligadas).
const groups = computed(() =>
  APPROVALS_QUEUE_GROUPS.map((group) => ({
    ...group,
    queues: queuesInGroup(queues.value, group.key)
  })).filter((group) => group.queues.length > 0)
)

const countFor = (key: ApprovalsQueueKey) => hubStore.countFor(key)
// Rodapé real (backend) quando a fila já o tem; senão o texto do registro.
const footerOf = (queue: ApprovalsQueue) => hubStore.footerFor(queue.key) ?? queue.footer

onMounted(() => {
  headerStore.setTitle('Aprovações', 'Escolha a fila que você quer analisar')
  // Tolerante a backend antigo: falha volta ao default (nenhuma fila aposentada).
  void dsAttendanceStore.fetchRegistrationSettings()
  // Uma request leve por fila; falha vira "—" no card sem travar a navegação.
  void hubStore.fetchCounts(queues.value.map((queue) => queue.key))
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
      return (_openBlock(), _createElementBlock("section", {
        key: group.key,
        class: "flex flex-col gap-4",
        "data-testid": `approvals-hub-group-${group.key}`
      }, [
        _createElementVNode("div", null, [
          _createElementVNode("h2", _hoisted_3, _toDisplayString(group.title), 1),
          _createElementVNode("p", _hoisted_4, _toDisplayString(group.note), 1)
        ]),
        _createElementVNode("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.queues, (queue) => {
            return (_openBlock(), _createBlock(ApprovalQueueCard, {
              key: queue.key,
              icon: queue.icon,
              title: queue.cardTitle,
              description: queue.cardDescription,
              "count-label": queue.counterLabel,
              count: countFor(queue.key).value,
              "is-loading": countFor(queue.key).isLoading,
              "error-message": countFor(queue.key).error,
              badge: queue.badge ?? null,
              "footer-text": footerOf(queue)?.text ?? null,
              "footer-icon": footerOf(queue)?.icon ?? null,
              "footer-variant": footerOf(queue)?.variant ?? 'default',
              "test-id": queue.testId,
              onClick: ($event: any) => (_unref(router).push(queue.path))
            }, null, 8, ["icon", "title", "description", "count-label", "count", "is-loading", "error-message", "badge", "footer-text", "footer-icon", "footer-variant", "test-id", "onClick"]))
          }), 128))
        ])
      ], 8, _hoisted_2))
    }), 128)),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(panels.value, (panel) => {
      return (_openBlock(), _createElementBlock("section", {
        key: panel.key,
        class: "flex flex-col gap-4",
        "data-testid": `approvals-hub-group-${panel.key}`
      }, [
        _createElementVNode("div", null, [
          _createElementVNode("h2", _hoisted_7, _toDisplayString(panel.groupTitle), 1),
          _createElementVNode("p", _hoisted_8, _toDisplayString(panel.groupNote), 1)
        ]),
        _createVNode(ApprovalPanelCard, {
          icon: panel.icon,
          title: panel.title,
          description: panel.description,
          "test-id": panel.testId,
          onClick: ($event: any) => (_unref(router).push(panel.path))
        }, null, 8, ["icon", "title", "description", "test-id", "onClick"])
      ], 8, _hoisted_6))
    }), 128))
  ]))
}
}

})